import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { courseService } from "../../services/course.service";
import Layout from "../../shared/Layout";
import { Modal, ThemeModal } from "../../shared/ui/Modal/Modal";
import { CourseDataInfo, CourseMaterial } from "../../types";
import { NextWindowContext } from "./context/NextWindowContext";
import cls from "./LearnDashboard.module.scss";
import { LearnDashboardNavmenu } from "./ui/LearnDashboardNavmenu/LearnDashboardNavmenu";
import { LearnDashboardSidebar } from "./ui/LearnDashboardSidebar/LearnDashboardSidebar";
import { ArticleComponent } from "./ui/LearnDashboardView/ArticleComponent/ArticleComponent";
import { SlidesComponent } from "./ui/LearnDashboardView/SlidesComponent/SlidesComponent";
import { TaskComponent } from "./ui/LearnDashboardView/TaskComponent/TaskComponent";
import { TestComponent } from "./ui/LearnDashboardView/TestComponent/TestComponent";
import { ReactPlayerComponent } from "./ui/ReactPlayer/ReactPlayerComponent";
import Header from "../../shared/Header";

const useFetchCourseParts = (courseId: string) => {
  return useQuery<CourseMaterial | undefined, Error>({
    queryKey: ["courseInfo", courseId],
    queryFn: () => courseService.getCourseMaterial(courseId),
  });
};
const useFetchCourseInfo = (courseId: string) => {
  return useQuery<CourseDataInfo | undefined, Error>({
    queryKey: ["courseDetails", courseId],
    queryFn: () => courseService.getCourseInfo(courseId),
  });
};
interface ContentWindowParams {
  contentType: "article" | "video" | "slide" | "test" | "task";
  contentData: any;
}
export const LearnDashboard = () => {
  const isMobile = useMediaQuery("(max-width:1000px)");
  const { courseId } = useParams<{ courseId?: string }>();
  const [collapsed, setCollapsed] = useState(false);
  const [passedPart, setPassedPart] = useState<any>([]);
  const [isFinished, setIsFinished] = useState(false);
  const getPassedPart = useCallback(() => {
    if (localStorage.getItem("passedPart")) {
      setPassedPart(JSON.parse(localStorage.getItem("passedPart") ?? ""));
    }
  }, []);

  useEffect(() => {
    getPassedPart();
  }, [getPassedPart]);

  const [isAuthModal, setIsAuthModal] = useState(false);
  const onToggleModal = useCallback(() => {
    setIsAuthModal((prev) => !prev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: courseInfo,
    isLoading: isLoadingInfo,
    isError: isErrorInfo,
    error: errorInfo,
  } = useFetchCourseInfo(courseId || "");
  const {
    data: courseMaterial,
    isLoading: isLoadingMaterial,
    isError: isErrorMaterial,
    error: errorMaterial,
  } = useFetchCourseParts(courseId || "");
  console.log(courseMaterial?.data);

  const [contentWindow, setContentWindow] =
    useState<ContentWindowParams | null>(null);

  const getStorageKey = useCallback(
    () => `lastOpenedContent-${courseId}`,
    [courseId]
  );

  useEffect(() => {
    const storageKey = getStorageKey();
    const savedContent = localStorage.getItem(storageKey);
    if (savedContent) {
      const lastContentData = JSON.parse(savedContent);
      setContentWindow(lastContentData);
    }
  }, [getStorageKey]);

  const handleOpenContentWindow = useCallback(
    (
      contentType: "article" | "video" | "slide" | "test" | "task",
      contentData: any
    ) => {
      setContentWindow({ contentType, contentData });
      const storageKey = getStorageKey();
      localStorage.setItem(
        storageKey,
        JSON.stringify({ contentType, contentData })
      );
    },
    [getStorageKey]
  );

  useEffect(() => {
    const storageKey = getStorageKey();
    const lastOpenedContent = JSON.parse(
      localStorage.getItem(storageKey) || "{}"
    );
    if (lastOpenedContent && lastOpenedContent.contentType) {
      handleOpenContentWindow(
        lastOpenedContent.contentType,
        lastOpenedContent.contentData
      );
    }
  }, [handleOpenContentWindow, getStorageKey]);

  const handlePutPassedPart = useCallback(
    (contentData: any) => {
      const hasPassedPart = passedPart.find((el: any) => {
        let ans = true;
        for (const [key, value] of Object.entries(el)) {
          if (contentData[key] !== value) {
            ans = false;
          }
        }
        return ans;
      });
      if (!hasPassedPart) {
        setPassedPart([...passedPart, contentData]);
        localStorage.setItem(
          "passedPart",
          JSON.stringify([...passedPart, contentData])
        );
      }
    },
    [passedPart]
  );

  const nextWindow = useCallback(() => {
    if (contentWindow) {
      let temp: any = [];
      courseMaterial?.data.forEach((el) => {
        el.items.forEach((item: any) => {
          temp.push(item);
        });
      });
      const currentId = temp.findIndex((el: any) => {
        let ans = true;
        for (const [key, value] of Object.entries(contentWindow?.contentData)) {
          if (el[key] !== value) {
            ans = false;
          }
        }
        return ans;
      });
      if (currentId >= temp.length - 2) {
        setIsFinished(true);
      }
      if (currentId >= temp.length - 1) {
        onToggleModal();
      } else {
        let contentType: "article" | "video" | "slide" | "test" | "task";
        const item = temp[currentId + 1];
        if (item.video && item.video_name && item.video_extension) {
          contentType = "video";
        } else if (
          item.slides &&
          item.slides_file_extension &&
          item.slides_file_name
        ) {
          contentType = "slide";
        } else if (item.test_name && item.questions) {
          contentType = "test";
        } else if (item.exercise_name && item.instructions) {
          contentType = "task";
        } else {
          contentType = "article";
        }
        handleOpenContentWindow(contentType, item);
        handlePutPassedPart(temp[currentId]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentWindow, courseMaterial, handlePutPassedPart]);

  const renderContentWindow = (
    contentWindowParams: ContentWindowParams | null
  ) => {
    if (!contentWindowParams) return null;

    switch (contentWindowParams.contentType) {
      case "article":
        return (
          <ArticleComponent
            data={contentWindowParams.contentData}
            isFinished={isFinished}
          />
        );
      case "video":
        return (
          <ReactPlayerComponent
            data={contentWindowParams.contentData}
            setCollapsed={setCollapsed}
            collapsed={collapsed}
            showNext={true}
            isFinished={isFinished}
          />
        );
      case "slide":
        return (
          <SlidesComponent
            data={contentWindowParams.contentData}
            isFinished={isFinished}
          />
        );
      case "test":
        return (
          <TestComponent
            data={contentWindowParams.contentData}
            isFinished={isFinished}
          />
        );
      case "task":
        return (
          <TaskComponent
            setCollapsed={setCollapsed}
            collapsed={collapsed}
            data={contentWindowParams.contentData}
            isFinished={isFinished}
          />
        );
      default:
        return <Box>Тип контента не поддерживается</Box>;
    }
  };

  if (isLoadingInfo || isLoadingMaterial) {
    return (
      <Box>
        <LinearProgress />
      </Box>
    );
  }
  if (isErrorInfo || isErrorMaterial) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}>
        <Typography>
          Ошибка: {errorInfo?.message || errorMaterial?.message}
        </Typography>
      </Box>
    );
  }
  if (!courseInfo || !courseMaterial) {
    return (
      <Layout>
        <Box>Курс не найден</Box>
      </Layout>
    );
  }
  return (
    <div className={"studyDashboard"}>
      <Modal
        theme={ThemeModal.PRIMARY}
        isOpen={isAuthModal}
        onClose={onToggleModal}>
        <Typography fontSize={20}>Вы прошли курс!</Typography>
      </Modal>

      {!isMobile && (
        <LearnDashboardNavmenu
          setPassedPart={setPassedPart}
          courseInfo={courseMaterial}
        />
      )}

      {isMobile && <Header />}

      <div className={cls.content}>
        <LearnDashboardSidebar
          setCollapsed={setCollapsed}
          collapsed={collapsed}
          courseMaterial={courseMaterial}
          onItemSelect={handleOpenContentWindow}
          passedPart={passedPart}
        />
        {isLoadingMaterial ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              padding: "20px",
              overflow: "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              paddingTop: "35vh",
            }}>
            <CircularProgress />
          </Box>
        ) : (
          <NextWindowContext.Provider value={nextWindow}>
            {renderContentWindow(contentWindow)}
          </NextWindowContext.Provider>
        )}
      </div>
    </div>
  );
};
